import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { siteData } from '../data/mock';

export const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const scrollToBook = () => {
    const el = document.querySelector('#book');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#0d0f12' }}
    >
      {/* Decorative circuit lines background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0,212,255,0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,212,255,0.3) 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />
        {/* Red diagonal lines mimicking book cover */}
        <div
          className="absolute top-0 right-0 w-[60%] h-full opacity-[0.04]"
          style={{
            background: `
              linear-gradient(145deg, transparent 40%, rgba(220,50,50,0.4) 41%, transparent 42%),
              linear-gradient(35deg, transparent 55%, rgba(220,50,50,0.3) 56%, transparent 57%)
            `,
          }}
        />
        {/* Cyan glow from top-right */}
        <div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.06]"
          style={{
            background: 'radial-gradient(circle, #00d4ff, transparent 70%)',
          }}
        />
      </div>

      <div className="relative z-10 max-w-[87.5rem] mx-auto w-full px-5 md:px-10 py-24 md:py-40">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-24">
          {/* Text Content */}
          <div className="flex-1 flex flex-col items-start">
            <div
              className={`transition-all duration-1000 delay-200 ${
                loaded
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <p className="font-montserrat text-xs tracking-[0.3em] uppercase text-[#888680] mb-6">
                Decoding a Killer Written in Scripture and Silicon
              </p>
            </div>

            <h1
              className={`font-montserrat font-900 text-[clamp(2.5rem,5vw,5.5rem)] leading-[0.85] uppercase text-[#e8e6e1] mb-6 md:mb-8 tracking-tight transition-all duration-1000 delay-400 ${
                loaded
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="block">G.S.</span>
              <span className="block text-[#00d4ff]">Rylan</span>
            </h1>

            <p
              className={`font-cardo text-lg md:text-xl text-[#888680] max-w-md leading-relaxed mb-4 italic transition-all duration-1000 delay-500 ${
                loaded
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              {siteData.author.tagline}
            </p>

            <p
              className={`font-cardo text-base md:text-lg text-[#666] max-w-lg leading-relaxed mb-12 transition-all duration-1000 delay-600 ${
                loaded
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              {siteData.author.heroSubtitle}
            </p>

            <div
              className={`transition-all duration-1000 delay-700 ${
                loaded
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <button
                onClick={scrollToBook}
                className="btn-primary font-montserrat font-semibold text-sm tracking-wider uppercase px-10 py-4 rounded-full bg-[#00d4ff] text-[#0d0f12] border-none cursor-pointer transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(0,212,255,0.2)] active:scale-[0.97]"
              >
                Discover The Silent Cipher
              </button>
            </div>
          </div>

          {/* Book Cover */}
          <div
            className={`flex-shrink-0 relative transition-all duration-1200 delay-600 ${
              loaded
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-12'
            }`}
          >
            {/* Glow behind book */}
            <div
              className="absolute -inset-8 opacity-20 blur-3xl"
              style={{
                background:
                  'radial-gradient(ellipse, #00d4ff 0%, transparent 70%)',
              }}
            />
            <img
              src={siteData.images.bookCover}
              alt="The Silent Cipher - Book Cover"
              className="relative z-10 w-[240px] md:w-[340px] lg:w-[380px] shadow-2xl"
              style={{
                boxShadow:
                  '0 25px 60px rgba(0,0,0,0.6), 0 0 80px rgba(0,212,255,0.08)',
              }}
            />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="font-montserrat text-[10px] tracking-[0.2em] uppercase text-[#555]">Scroll</span>
        <ChevronDown size={16} className="text-[#555]" />
      </div>
    </section>
  );
};
